<template>
  <p
    :ref="`P_${registrationId}`"
    @mouseup="$emit('paragraphMouseUp', $event)"
    :id="`${elementIdPrefix}_P_${paragraphId}`"
    @mouseenter="mouseEnter"
    :class="{ 'uk-margin-remove': removeMargin }"
  >
    <template v-for="(span, idx) in paragraphSpans" :key="idx">
      <template v-if="span.counterpointAnnotationIDs.length">
        <span
          style="display: inline-block"
          v-for="n in span.nodes"
          :key="`P_${registrationId}_S_${idx}_N_${n.nodeKey}`"
          :id="`${elementIdPrefix}_P_${paragraphId}_S_${idx}_N_${n.nodeKey}`"
          :ref="`P_${registrationId}_S_${idx}`"
          :class="{
            hover: hover ? hover(n) : false,
            'annotation-markup': cachedShowAnnotationMarkup[idx],
            [cachedCounterpointIDClasses[idx]]: true,
            'annotation-selected': annotationSelected(span),
            'isHedgingAnalytic-selected':
              hedgingOnOff && !whitespaceNodes[n.nodeKey] ? hasAnalyticsData(span) : false,
          }"
          @mouseenter="mouseEnterNodeOrSpan(n)"
          @click="
            handleSingleAndDoubleClick(
              $event,
              ev => $emit('mouseClickNode', n),
              ev => $emit('dblclick', cachedCounterpointIDClasses[idx])
            )
          "
        >
          {{ whitespaceNodes[n.nodeKey] ? '&nbsp;' : n.nodeContent }}

          <template v-if="nodeHasAnnotations(n)">
            <span
              v-for="cpData in nodeAnnotations[n.nodeKey].counterpoints"
              :key="cpData.id"
              class="annotation-highlight"
              :class="{
                [`annotation-highlight-${cpData._id}`]: !!cpData,
              }"
              :style="{
                background: getCounterpointColor(cpData),
              }"
            >
            </span>
          </template>
        </span>
      </template>

      <template v-else-if="hasAnalyticsData(span)">
        <template class="uk-inline">
          <span
            :key="`P_${registrationId}_S_${idx}`"
            @mouseleave="hideModal"
            @mouseenter="showAnalyticModal(span.nodes)"
          >
            <span
              style="display: inline-block"
              v-for="(n, nIdx) in span.nodes"
              :key="`P_${registrationId}_S_${idx}_N_${n.nodeKey}_${nIdx}`"
              :id="`${elementIdPrefix}_P_${paragraphId}_S_${idx}_N_${n.nodeKey}`"
              :ref="`P_${registrationId}_S_${idx}`"
              @dblclick="$emit('dblclick')"
              @mouseenter="mouseEnterNodeOrSpan(n)"
              :class="{
                hover: hover ? hover(n) : false,
                'isHedgingAnalytic-selected':
                  hedgingOnOff && !whitespaceNodes[n.nodeKey] ? hasAnalyticsData(span) : false,
              }"
            >
              {{ whitespaceNodes[n.nodeKey] ? '&nbsp;' : n.nodeContent }}
            </span>
          </span>

          <template
            v-for="(sourceType, key) in documentSourceAnalytics"
            :key="`${elementIdPrefix}_P_${paragraphId}_S_${idx}_H_${key}`"
          >
            <div
              v-if="shouldRenderStyle(span.nodes, sourceType.sourceType)"
              class="hedgingStyle"
              :class="{ 'uk-open': tagDescriptorData[sourceType.sourceType] }"
              uk-dropdown
              :style="dropdownStyle"
              @mouseenter="adjustDropdownPosition"
            >
              <h4 class="hedgingStyleHead">{{ tagDescriptorHead[sourceType.sourceType] }}</h4>
              <hr color="#faaf5a" size="5px" class="hedginHr" />
              {{ tagDescriptorData[sourceType.sourceType] }}
            </div>
          </template>
        </template>
      </template>

      <template v-else-if="canEditOutline">
        <span
          :key="`P_${registrationId}_S_${idx}`"
          :id="`${elementIdPrefix}_P_${paragraphId}_S_${idx}`"
          @mouseenter="mouseEnterNodeOrSpan(span)"
          @dblclick="$emit('dblclick')"
          :class="{
            'markup-highlihgt--neutral': true,
            hover: hover ? hover(span) : false,
            'text-selected': isTextSelected(span, elementIdPrefix),
          }"
          >{{ span.fullText }}</span
        >
      </template>
      <template v-else>
        <span
          :style="{
            display: mainPanelView === 'article' && !counterpointLoaded ? 'inline-block' : 'inline',
          }"
          :key="`P_${registrationId}_S_${idx}`"
          :id="`${elementIdPrefix}_P_${paragraphId}_S_${idx}`"
          @mouseenter="mouseEnterNodeOrSpan(span)"
          @dblclick="$emit('dblclick')"
          :class="{
            'markup-highlihgt--neutral': true,
            hover: hover ? hover(span) : false,
            'text-selected': isTextSelected(span, elementIdPrefix),
          }"
          >{{ span.fullText }}
        </span>
      </template>
    </template>
  </p>
</template>

<script>
import { generateUuid } from '@azure/core-http';
import { mapGetters, mapState } from 'vuex';
import { getElementConstrainedPositionAndGeometry } from '@/assets/js/utils';
import { singleDoubleClicksMixin } from '@/assets/js/mixins/singleDoubleClicksMixin';
import _ from 'lodash';

export default {
  name: 'AnnotatedParagraph',
  mixins: [singleDoubleClicksMixin],
  props: {
    paragraph: [Object, Array],
    hover: Function,
    elementIdPrefix: String,
    geometryTerminalElementId: String,
    nodeAnnotations: Object,
    vertical: Boolean,
    removeMargin: Boolean,
    annHovered: Boolean,
  },
  emits: [
    'mouseEnterSpan',
    'mouseEnterNode',
    'onScreenChanged',
    'geometriesUpdated',
    'paragraphMouseUp',
    'mouseClickNode',
    'dblclick',
    'mouseEnterNodeOrSpan',
    'hoverIn',
    'hoverOut',
  ],
  data() {
    return {
      registrationId: '',
      currentGeometries: {},
      mouseEnterSet: false,
      tagDescriptorData: {},
      tagDescriptorHead: {},
      hedgingStyleRendered: [],
      showHideValue: false,
      dropdownStyle: {},
      // Cached computed properties
      cachedCounterpointIDClasses: [],
      cachedShowAnnotationMarkup: [],
    };
  },
  mounted() {
    this.registrationId = generateUuid();
    window.addEventListener('resize', () => {
      const currentGeometries = this.geometries();
      this.$emit('geometriesUpdated', currentGeometries);
    });
  },
  updated() {
    const currentGeometries = this.geometries();
    if (!_.isEqual(currentGeometries, this.currentGeometries)) {
      this.currentGeometries = currentGeometries;
      this.$emit('geometriesUpdated', this.currentGeometries);
    }
    this.handleTextSelected();
  },
  beforeUnmount() {
    window.removeEventListener('resize', () => {
      const currentGeometries = this.geometries();
      this.$emit('geometriesUpdated', currentGeometries);
    });
  },

  computed: {
    ...mapState([
      'annotationHoverIds',
      'publicView',
      'showingCounterpointSidePanel',
      'verticalView',
      'newAnnotationPosition',
      'counterpointLoaded',
      'mainPanelView',
      'hedgingOnOff',
      'showingCounterpointSidePanel',
      'analyticHideShow',
    ]),
    ...mapGetters([
      'canEditOutline',
      'getCounterpointColor',
      'documentSourceAnalytics',
      'roundCount',
    ]),
    paragraphId() {
      return this.paragraph?.paragraphId;
    },
    groundedId() {
      return this.paragraph?.spans[0].secondPart;
    },
    paragraphSpans() {
      return this.paragraph?.spans || this.paragraph;
    },
    whitespaceNodes() {
      const whitespaceNodes = {};
      for (const n of this.paragraph.nodes) {
        if (n.nodeContent.trim() === '') whitespaceNodes[n.nodeKey] = true;
      }
      return whitespaceNodes;
    },
    annotationSelected() {
      const hoverIds = Object.keys(this.annotationHoverIds);
      if (hoverIds.length === 0) return () => false;

      const hoverIdSet = new Set(hoverIds);
      return span => span.counterpointAnnotationIDs.some(id => hoverIdSet.has(id));
    },
    paragraphElement() {
      return this.$refs[`P_${this.registrationId}`];
    },
  },
  watch: {
    // whenever question changes, this function will run
    showingCounterpointSidePanel(newValue) {
      this.showHideValue = newValue;
      if (newValue) {
        const currentGeometries = this.geometries();
        this.$emit('geometriesUpdated', currentGeometries);
      }
    },
    annHovered(value) {
      if (value) {
        setTimeout(() => {
          this.handleTextSelected();
        }, 500);
      }
    },
    paragraph: {
      handler() {
        this.updateCachedValues();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    updateCachedValues() {
      if (!this.paragraphSpans) {
        this.cachedCounterpointIDClasses = [];
        this.cachedShowAnnotationMarkup = [];
        return;
      }

      this.cachedCounterpointIDClasses = this.paragraphSpans.map(span => {
        const classes = span.counterpointAnnotationIDs
          .map(annId => `${this.vertical ? 'vertical-' : ''}counterpoint-ref-${annId}`)
          .join(' ');
        return classes;
      });

      this.cachedShowAnnotationMarkup = this.paragraphSpans.map(
        span => span.counterpointAnnotationIDs.length > 0
      );
    },
    hasAnalyticsData(span) {
      for (const source in this.documentSourceAnalytics) {
        if (span[source]) {
          return true;
        }
      }
      return false;
    },
    nodeHasAnnotations(node) {
      return this.nodeAnnotations[node.nodeKey].counterpointAnnotationIDs.length;
    },
    showAnalyticModal(nodes) {
      for (const source in this.documentSourceAnalytics) {
        const relevantNodes = nodes.filter(
          node => this.hedgingOnOff && node.nodeKey && this.nodeHasAnalytics(node, source)
        );
        let analyticData = this.documentSourceAnalytics[source];
        let showAnalyticData = this.analyticHideShow.map(n => n.name);
        if (analyticData && relevantNodes.length !== 0 && showAnalyticData.includes(source)) {
          let node = analyticData?.nodes?.find(node =>
            node.basisNodes?.some(basisNode => basisNode.nodeKey === relevantNodes[0]?.nodeKey)
          );
          let data = node ? node.explanation : null;
          let tag = node ? node.tag : null;
          this.tagDescriptorData[source] = data || {};
          this.tagDescriptorHead[source] = tag || {}; // $
        }
      }
    },
    mouseEnterNodeOrSpan(nodeOrSpan) {
      this.handleTextSelected();
      this.$emit('mouseEnterNodeOrSpan', nodeOrSpan);
    },
    hideModal() {
      this.tagDescriptorData = {};
      this.tagDescriptorHead = {};
    },
    adjustDropdownPosition(event) {
      // Calculate the new position based on the mouse event
      const newPosition = {
        top: `${event.clientY}px`, // Set the top position
        left: `${event.clientX}px`, // Set the left position
      };
      this.dropdownStyle = newPosition;
    },
    shouldRenderStyle(nodes, sourceType) {
      const shouldRender = this.tagDescriptorData[sourceType] && this.hedgingOnOff;
      const relevantNodes = nodes.filter(
        node => this.hedgingOnOff && node.nodeKey && this.nodeHasAnalytics(node, sourceType)
      );
      if (shouldRender && relevantNodes.length > 0) {
        const idx = this.paragraphSpans.findIndex(span => span.nodes.includes(relevantNodes[0]));
        const alreadyRendered = this.hedgingStyleRendered.includes(idx);
        if (!alreadyRendered) {
          this.hedgingStyleRendered.push(idx);
        }
        return shouldRender && this.hedgingStyleRendered[0] === idx;
      }
      return false;
    },
    handleTextSelected() {
      const parent = this.$refs[`P_${this.registrationId}`];
      if (!parent) return;

      const children = Array.from(parent.children).filter(child =>
        Array.from(child.classList).some(cls => cls.startsWith('counterpoint-ref-'))
      );

      if (children.length === 0) return;

      // Group children by line (y-coordinate)
      const lines = new Map();
      for (const child of children) {
        const rect = child.getBoundingClientRect();
        if (!lines.has(rect.y)) {
          lines.set(rect.y, []);
        }
        lines.get(rect.y).push({ element: child, x: rect.x });
      }

      // Process each line
      for (const lineChildren of lines.values()) {
        // Sort children by x-coordinate
        lineChildren.sort((a, b) => a.x - b.x);

        // Reset classes
        for (const { element } of lineChildren) {
          element.classList.remove(
            'text-select-highlight-round-same-ann',
            'text-select-highlight-round-begin-ann',
            'text-select-highlight-round-end-ann'
          );
        }

        // Group by annotation ID
        const annotationsOnLine = new Map();
        for (const { element } of lineChildren) {
          const annClass = Array.from(element.classList).find(cls =>
            cls.startsWith('counterpoint-ref-')
          );
          if (annClass) {
            if (!annotationsOnLine.has(annClass)) {
              annotationsOnLine.set(annClass, []);
            }
            annotationsOnLine.get(annClass).push(element);
          }
        }

        // Apply new classes
        for (const elements of annotationsOnLine.values()) {
          if (elements.length === 1) {
            elements[0].classList.add('text-select-highlight-round-same-ann');
          } else if (elements.length > 1) {
            elements[0].classList.add('text-select-highlight-round-begin-ann');
            elements[elements.length - 1].classList.add('text-select-highlight-round-end-ann');
          }
        }
      }
    },

    mouseEnter() {
      this.$emit('hoverIn');
      this.mouseEnterSet = true;
    },

    mouseLeave() {
      this.$emit('hoverOut');
      this.mouseEnterSet = false;
    },
    nodeHasAnalytics(node, sourceType) {
      let annotationData = this.documentSourceAnalytics[sourceType];
      if (!annotationData) return false;
      let basisNodeKeys = annotationData?.nodes?.flatMap(node =>
        node.basisNodes?.map(basisNode => basisNode.nodeKey)
      );
      return basisNodeKeys?.some(basisNodeKey => basisNodeKey === node.nodeKey);
    },

    isTextSelected(span, elementIdPrefix) {
      if (elementIdPrefix === 'annotation') return false;
      if (this.roundCount <= 1 && this.showingCounterpointSidePanel) {
        let selectedData =
          span.nodes.filter(
            data =>
              parseInt(data.nodeKey.split('-')[0].slice(1)) >=
                parseInt(this.newAnnotationPosition?.from?.nodeKey.split('-')[0].slice(1)) &&
              parseInt(data.nodeKey.split('-')[1]) <=
                parseInt(this.newAnnotationPosition?.to?.nodeKey.split('-')[1])
          ).length > 0;
        if (
          selectedData &&
          elementIdPrefix === 'article' &&
          this.roundCount <= 1 &&
          this.showHideValue === true
        ) {
          let id = span.nodes[0]?.paragraphId;
          let articleElem = document.getElementById(`article_P_${id}`);
          articleElem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
        return selectedData;
      } else {
        return false;
      }
    },

    annotationElementMapping() {
      if (!this.paragraphSpans) return [];

      const nodesBySpanAnnotationId = this.paragraphSpans
        .flatMap((span, idx) =>
          span.counterpointAnnotationIDs.length
            ? span.counterpointAnnotationIDs.map(annotationId => {
                return {
                  annotationId,
                  elements: this.$refs[`P_${this.registrationId}_S_${idx}`],
                };
              })
            : {}
        )
        .filter(x => x.elements);
      return nodesBySpanAnnotationId.reduce(
        (annotationElementMapping, spanAnnotationElementMapping) => {
          return {
            ...annotationElementMapping,
            [spanAnnotationElementMapping.annotationId]: annotationElementMapping[
              spanAnnotationElementMapping.annotationId
            ]
              ? [
                  ...annotationElementMapping[spanAnnotationElementMapping.annotationId],
                  ...spanAnnotationElementMapping.elements,
                ]
              : spanAnnotationElementMapping.elements,
          };
        },
        {}
      );
    },

    annotationAnchorElements() {
      const annotationAnchorElements = {};
      const annotationElementMapping = this.annotationElementMapping();
      for (const key in annotationElementMapping) {
        const allElementsWithGeometry = annotationElementMapping[key].map(element => {
          return { element, geometry: element.getBoundingClientRect() };
        });
        const topElement = allElementsWithGeometry[0];
        const firstLine = allElementsWithGeometry.filter(
          x => x.geometry.top === topElement.geometry.top
        );
        const middleElementIndex = Math.floor(firstLine.length / 2);
        annotationAnchorElements[key] = {
          first: topElement?.element,
          middle: firstLine[middleElementIndex]?.element,
        };
      }
      return annotationAnchorElements;
    },

    geometries() {
      const geometries = {};
      let annotationAnchorElements = this.annotationAnchorElements();

      Object.keys(annotationAnchorElements).forEach(key => {
        const firstElementId = annotationAnchorElements[key]?.first?.id;
        const element = annotationAnchorElements[key]?.middle;
        let geometryBase;

        if (this.geometryTerminalElementId !== 'outlineVirtualScrollContainer') {
          geometryBase = getElementConstrainedPositionAndGeometry(
            element,
            this.geometryTerminalElementId
          );
        } else {
          geometryBase = {
            top: element?.offsetTop || 0,
            left: (element?.offsetLeft || 0) + element?.offsetWidth,
            height: element?.offsetHeight || 0,
          };
        }

        if (this.publicView) {
          geometryBase.top = geometryBase.top - 45;
        } else if (this.geometryTerminalElementId !== 'articleVirtualScrollContainer') {
          geometryBase.top = geometryBase.top - 36;
        } else if (this.geometryTerminalElementId === 'articleVirtualScrollContainer') {
          geometryBase.top = geometryBase.top - 8;
        }

        if (this.showingCounterpointSidePanel) {
          geometryBase.left =
            geometryBase.left - (document.getElementById('outlineContainer')?.offsetWidth || 0);
        }

        geometries[key] = {
          nodeKey: firstElementId?.match(/(Node_|G|T)[\0-9]+/)[0],
          ...geometryBase,
        };
      });
      return geometries;
    },
  },
};
</script>

<style lang="scss" scoped>
.text-selected {
  background: rgba(200, 221, 233, 0.5);
  -moz-border-top-left-radius: 15px;
  -webkit-border-top-left-radius: 15px;
  -moz-border-bottom-left-radius: 15px;
  -webkit-border-bottom-left-radius: 15px;
  border-bottom-left-radius: 15px;
  -moz-border-bottom-right-radius: 15px;
  -webkit-border-bottom-right-radius: 15px;
  border-bottom-right-radius: 15px;
  -moz-border-top-right-radius: 15px;
  -webkit-border-top-right-radius: 15px;
  border-top-right-radius: 15px;
  -webkit-box-decoration-break: clone;
  -o-box-decoration-break: clone;
  box-decoration-break: clone;
}

.text-select-highlight-round-same-ann span {
  border-radius: 20px;
  width: calc(100% + 14px);
  left: -7px;
}

.text-select-highlight-round-begin-ann span {
  border-radius: 20px 0 0 20px;
  width: calc(100% + 7px);
  left: -7px;
}

.text-select-highlight-round-end-ann span {
  border-radius: 0 20px 20px 0;
  width: calc(100% + 7px);
}

.isHedgingAnalytic-selected {
  text-decoration: underline;
  text-decoration-color: #faa05a;
  text-decoration-style: dotted;
  text-decoration-line: underline;
  text-underline-position: under;
}

.side-open {
  left: 200px !important;
}

.hedgingStyle {
  border-radius: 20px;
  max-width: 40px !important;
  border: 2px solid #faa05a;
  font-size: $global-small-font-size;
  position: absolute;
  padding: 11px !important;
  user-select: none;
  z-index: 999;
  overflow: hidden;
}

.hedgingStyleHead {
  color: #faa05a;
  text-align: center;
  margin: 0 !important;
}

.hedginHr {
  margin: 0 !important;
}
</style>
